import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { Button, Loader, PractitionerOverviewCard } from "@mwi/ui";
import { setStep } from "slices/booking";
import { useGetPractitionerAvailabilityQuery } from "api";
import { AvailablePractitioner } from "api/types/models/availability";

export interface NoPreferenceCardProps {
  onClick?: () => void;
}

const BookingFlowStep2a = (): JSX.Element => {
  const dispatch = useDispatch();
  const [selectedPractitioner, setSelectedPractitioner] =
    useState<AvailablePractitioner | null>(null);

  const { patients, registeredSite, vetSite } = useSelector(
    (state: RootState) => ({
      patients: state.bookingFlow.formState.patients,
      registeredSite: state.auth.registered_site,
      vetSite: state.bookingFlow.formState.vet_site_uuid,
    }),
  );

  const {
    isLoading,
    data: practitionerAvailability,
    isFetching: fetchingPractitionerAvailability,
    isError: practitionerAvailabilityError,
  } = useGetPractitionerAvailabilityQuery({
    reasons: patients?.map((p) => p?.reason_uuid) ?? [],
    patients: patients?.map((p) => p.patient_uuid) ?? [],
    location_uuid: vetSite ? vetSite : (registeredSite?.id ?? ""),
  });

  const handleSelectedPractitioner = (
    selectedPractitioner: AvailablePractitioner | null,
  ) => {
    setSelectedPractitioner(selectedPractitioner);
  };

  const continueClicked = () => {
    dispatch(
      setStep({
        step: "3",
        preferredPractitioner: selectedPractitioner,
      }),
    );
  };

  const noPreferenceClicked = () => {
    setSelectedPractitioner(null);
    dispatch(
      setStep({
        step: "3",
        preferredPractitioner: null,
      }),
    );
  };

  return (
    <>
      {isLoading && (
        <div
          aria-live="polite"
          role="status"
          className="grid h-full place-content-center"
        >
          <Loader />
        </div>
      )}

      {!isLoading && (
        <>
          <div className="mb-8">
            <h2 className="text-4xl font-bold leading-[43px]">
              Is there a specific vet you'd like to see?
            </h2>
          </div>

          <div className="mt-auto mb-15">
            <Button block onClick={noPreferenceClicked}>
              No preference. Show all slots.
            </Button>
          </div>

          <div className="mt-10 mb-10">
            <p className="text-base">Select an option from the list below</p>
          </div>

          <div className="mb-[72px]">
            <div className="flex flex-col gap-4">
              {practitionerAvailability?.map((practitioner) => (
                <PractitionerOverviewCard
                  key={practitioner.contactUuid}
                  name={`${practitioner.firstName ?? ""} ${practitioner.lastName ?? ""}`.trim()}
                  onClick={() => handleSelectedPractitioner(practitioner)}
                  isSelected={selectedPractitioner === practitioner}
                />
              ))}
            </div>
          </div>

          <Button
            block
            onClick={continueClicked}
            disabled={selectedPractitioner === null}
          >
            Continue
          </Button>
        </>
      )}
    </>
  );
};

export default BookingFlowStep2a;
